"use client"

import type React from "react"
import { cn } from "@/lib/utils"
import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import dynamic from "next/dynamic"
import { BooruBrowser } from "@/components/booru-browser"
import { FollowingFeed } from "@/components/following-feed"
import { BooruDetailModal } from "@/components/booru-detail-modal"
import { SearchBar } from "@/components/search-bar"
import { FilterPanel } from "@/components/filter-panel"
import { TagSetManager } from "@/components/tag-set-manager"
import { MiniSearchBar } from "@/components/mini-search-bar"
import { TagStepGesture } from "@/components/tag-step-gesture"
import { effectiveSetTags } from "@/lib/tag-step"
import type { PostDateRange } from "@/lib/post-date"
import { SwipeTutorial } from "@/components/modals/swipe-tutorial"
import { SearchConsoleModal } from "@/components/search-console-modal"
import { SettingsPanel } from "@/components/settings-panel"
import { LanguageSelector } from "@/components/language-selector"
import { Folder, Gamepad2, Visor, Mouse, Pin, MessageSquare, Search, BookOpen, Camera, Palette, Store, Bug, Loader2 } from "@/lib/icons"
import { HugeiconsIcon } from "@hugeicons/react"
import { useRecentBrowseStore } from "@/lib/recent-browse-store"
import { PullDownHint, FiltersBubble } from "@/components/first-visit-intro"
import { Alien02Icon, LayoutDashboardIcon } from "@hugeicons/core-free-icons"
import { FeatureFlagWrapper } from "@/components/feature-flag-wrapper"
import { LibraryBrowser } from "@/components/library-browser"
import { PipPlayer } from "@/components/pip-player"
import type { BooruContentTypeVisibility, BooruPost, BooruSite } from "@/lib/booru-api"
import { useTagSetStore } from "@/lib/tag-set-store"
import { useUserProfileStore } from "@/lib/user-profile-store"
import { useSavedTagsStore } from "@/lib/saved-tags-store"
import { useSettingsStore } from "@/lib/settings-store"
import { useTagHistoryStore } from "@/lib/tag-history-store"
import { trackTagAffinity } from "@/lib/analytics-client"
import { useAuthStore } from "@/lib/auth-store"
import { fetchSeenState, markSeen } from "@/lib/client/seen-state"
import { Button } from "@/components/ui/button"
import { Shuffle, Dices, Settings, Eye, EyeOff, Sliders, ChevronUp, ChevronDown, ArrowLeft, HeartHandshake, Filter, X, Globe } from "@/lib/icons"
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet"
import { BatchDownloadModal } from "@/components/batch-download-modal"
import { ScriptMiniPlayer } from "@/components/script-mini-player"
import { DeviceManualIndicator } from "@/components/device-manual-indicator"
import { useViewStateStore } from "@/lib/view-state-store"
import { useScriptLinkingStore } from "@/lib/script-linking-store"
import { useBountyContentSelectionStore } from "@/lib/bounty-content-selection-store"
import { useCommissionContentSelectionStore } from "@/lib/commission-content-selection-store"
import { UserMenu } from "@/components/auth/user-menu"
import { PartnerLinksBar } from "@/components/partner-links-bar"
import { useRouter } from "next/navigation"
import { usePatreonMode } from "@/lib/use-patreon-mode"
import { PATREON_SUBSCRIBE_PATH } from "@/lib/patreon/paths"
import { useIsGuest } from "@/lib/auth-store"
import { useGlymUnread } from "@/hooks/use-glym-unread"
import { useUrlParamSetter } from "@/hooks/use-url-state"
import { useVRStore } from "@/lib/vr-store"
import { useSearchParams } from "next/navigation"
import { BooruAuthDialog } from "@/components/booru-auth-dialog"
import { useSourceStore } from "@/lib/source-store"
import { useBooruCredentialsStore } from "@/lib/booru-credentials-store"
import { toast } from "sonner"
import { FeedbackModal } from "@/components/feedback-modal"
import { useMarketplaceStore } from "@/lib/marketplace-store"
import { MembershipPromoStrip } from "@/components/membership-promo-strip"
import { PromoModal } from "@/components/modals/promo-modal"
import { FounderPromoModal } from "@/components/modals/founder-promo-modal"
import { FounderPlusModal } from "@/components/modals/founder-plus-modal"
import { DayOnePromoStack } from "@/components/modals/day-one-promo-stack"
import { useTranslations } from "next-intl"

// Code-split — each of these three is only ever rendered once viewMode
// matches it, but a static import still ships its full JS in the homepage's
// own bundle to every visitor regardless of which tab (if any) they open.
//
// ssr:false tradeoff, stated honestly: for the default "browse" landing
// state these were never server-rendered anyway (viewMode starts "browse"),
// so nothing changes. The one case it *does* change is a deep link that
// seeds viewMode from the URL — /?view=editors|gooncave|game-arena (see the
// useState initializer below) — which now paints the spinner below and
// fetches the chunk client-side instead of arriving in the server HTML.
// Accepted deliberately: these are authenticated app surfaces, not indexed
// content, so a sub-second chunk fetch on a rare deep link is a fair price
// for taking ~5,900 lines out of every visitor's initial bundle.
// See docs/PERFORMANCE_TASK_PLAN.md.
// h-full (not flex-1) on purpose: the <main> these render into is itself a
// flex item, but not a flex *container*, so flex-1 here would be inert and
// the spinner would pin to the top edge instead of centering.
const dynamicTabLoading = () => (
  <div className="h-full w-full flex items-center justify-center">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
)
const Gooncave = dynamic(() => import("@/components/gooncave").then((mod) => mod.Gooncave), {
  ssr: false,
  loading: dynamicTabLoading,
})
const GameArena = dynamic(() => import("@/components/game-arena").then((mod) => mod.GameArena), {
  ssr: false,
  loading: dynamicTabLoading,
})
// Highest-value split of the three: EditorsHub itself is modest (~770
// lines), but it internally imports CommissionDashboardV2 (~2,460 lines),
// so this one change also stops the commission dashboard from riding along
// in the homepage bundle.
const EditorsHub = dynamic(() => import("@/components/editors-hub").then((mod) => mod.EditorsHub), {
  ssr: false,
  loading: dynamicTabLoading,
})

// Same key convention BooruDetailModal already uses internally
// (previousModalPostKeyRef) — kept identical so the two stay comparable.
// Booru posts come from 20+ sources and most don't support "refetch a
// single post by id," so instead of putting the post in the URL directly we
// cache the whole object (already in memory the moment it's clicked) in
// sessionStorage and put only this key in the URL. Same-tab restore only —
// a fresh tab/device opening a bare ?post=... link with nothing cached just
// falls back to today's behavior (plain grid), never worse.
//
// Also caches `vr` (isVRMode at the moment of open/change) alongside the
// post — checked live (2026-09-22) that lib/vr-store.ts's persist config
// deliberately excludes isVRMode from its partialize list (only
// projectionType/stereoLayout/etc. survive a refresh on their own), so
// restoring the post alone does NOT bring VR mode back on its own the way
// earlier research assumed. This cache is what actually restores it.
interface SelectedPostCache {
  post: BooruPost
  vr: boolean
}
function selectedPostSessionKey(post: Pick<BooruPost, "id" | "site">) {
  return `${post.site ?? "unknown"}:${post.id}`
}
function readSelectedPostFromSession(key: string): SelectedPostCache | null {
  if (typeof window === "undefined") return null
  try {
    const raw = sessionStorage.getItem(`goonru:selected-post:${key}`)
    return raw ? (JSON.parse(raw) as SelectedPostCache) : null
  } catch {
    return null
  }
}

export default function Home() {
  const tt = useTranslations("pagePage")
  const glymUnread = useGlymUnread()
  const router = useRouter()
  const searchParams = useSearchParams()
  const isGuest = useIsGuest()
  const [selectedPost, setSelectedPost] = useState<BooruPost | null>(null)
  const [selectedPostIndex, setSelectedPostIndex] = useState<number>(-1)
  const setSelectedPostUrlParam = useUrlParamSetter("post")
  const isVRMode = useVRStore((state) => state.isVRMode)
  const setVRMode = useVRStore((state) => state.setVRMode)
  // Closing is always this explicit call, never inferred reactively from
  // selectedPost going null — an earlier version tried to infer it inside
  // the write effect below and raced against setVRMode's restore update
  // (zustand's own re-render lands on a different tick than React's batched
  // setSelectedPost), intermittently clearing the ?post= param mid-restore.
  const closeSelectedPost = useCallback(() => {
    setSelectedPost(null)
    setSelectedPostUrlParam(null)
  }, [setSelectedPostUrlParam])

  // Founder/membership promo state
  const [founderSlots, setFounderSlots] = useState({ claimed: 0, remaining: 250, total: 250 })
  const { enabled: patreonMode } = usePatreonMode()

  useEffect(() => {
    fetch("/api/founder/slots").then(r => r.json()).then(setFounderSlots).catch(() => {})
  }, [])
  
  // Use persisted selectedSites from settings store
  const selectedSites = useSettingsStore((state) => state.selectedSites)
  const setSelectedSites = useSettingsStore((state) => state.setSelectedSites)
  
  // Which ratings are switched on (persisted). Applied in the browser after the fetch, so changing it never triggers a
  // new search (the search request always asks for every rating).
  const activeRatings = useSettingsStore((state) => state.activeRatings)
  const setActiveRatings = useSettingsStore((state) => state.setActiveRatings)
  // Newest first by default. Score used to be the default, but it only reordered the posts already loaded; now that Score
  // really asks the source for its top posts, defaulting to it would turn every first search into "all-time best" and
  // sidestep the shared newest-first cache.
  const [sortBy, setSortBy] = useState<"score" | "date" | "random" | "rating" | "downloads" | "favorites" | "relevance" | "duration">("date")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc")
  const [randomSeed, setRandomSeed] = useState(() => Date.now())
  const [dateRange, setDateRange] = useState<PostDateRange | undefined>(undefined)
  const [allPosts, setAllPosts] = useState<BooruPost[]>([])
  const [hideUI, setHideUI] = useState(false)
  const [showMiniSearch, setShowMiniSearch] = useState(false)
  const [showSearchConsole, setShowSearchConsole] = useState(false)
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 })
  const [touchEnd, setTouchEnd] = useState({ x: 0, y: 0 })
  // Lazy-init: if we're mounting fresh because the user pressed Back from a page
  // reached mid-linking-flow (e.g. Search Goonfolio → a folio detail page), stay on
  // "collections" instead of defaulting to "browse" so Back actually returns them to
  // where they were, not the top of the app.
  const viewParam = searchParams.get("view")
  const [viewMode, setViewMode] = useState<"browse" | "collections" | "editors" | "gooncave" | "game-arena">(() => {
    if (viewParam === "game-arena" || viewParam === "gooncave" || viewParam === "collections" || viewParam === "editors") return viewParam
    return useScriptLinkingStore.getState().isLinkingMode ? "collections" : "browse"
  })
  const mainRef = useRef<HTMLDivElement>(null)
  const browseHeaderRef = useRef<HTMLElement>(null)
  const [showBatchDownloadFavorites, setShowBatchDownloadFavorites] = useState(false)
  const [favoritesPosts, setFavoritesPosts] = useState<BooruPost[]>([])
  const [intentionalViewChange, setIntentionalViewChange] = useState(false)
  const [contentTypeVisibility, setContentTypeVisibility] = useState<BooruContentTypeVisibility>({
    album: true,
    image: true,
    video: true,
  })
  // "GIFs only" (Filters, Content Filters): shows just the animated gifs; off by default and reset on reload like the other content filters.
  const [gifsOnly, setGifsOnly] = useState(false)
  const bountySelectionActive = useBountyContentSelectionStore((state) => state.active)
  const cancelBountySelection = useBountyContentSelectionStore((state) => state.cancel)
  const selectBountyPost = useBountyContentSelectionStore((state) => state.selectPost)
  const commissionSelectionActive = useCommissionContentSelectionStore((state) => state.active)
  const cancelCommissionSelection = useCommissionContentSelectionStore((state) => state.cancel)
  const selectCommissionPost = useCommissionContentSelectionStore((state) => state.selectPost)
  const prevViewModeRef = useRef<"browse" | "collections" | "editors" | "gooncave" | "game-arena">("browse")

  useEffect(() => {
    if (viewParam === "game-arena" || viewParam === "gooncave" || viewParam === "collections" || viewParam === "editors") {
      setViewMode(viewParam)
    }
  }, [viewParam])

  // Keep the URL's `view` param in sync with viewMode, in both directions --
  // this is the actual fix for "refresh inside Editors Hub / a tab like
  // Devices bounces to home": viewMode was pure in-memory React state with
  // no URL reflection on the WRITE side (the read side above already
  // existed and worked fine, which is why this went undiagnosed for a
  // while -- a refresh always looked identical to a fresh, un-navigated
  // load, because the URL genuinely never changed). Reacts to viewMode
  // itself rather than wrapping handleViewModeChange, since several call
  // sites (e.g. the returnToEditor / notification deep-link effects below)
  // call setViewMode directly and bypass that function entirely.
  useEffect(() => {
    const currentViewParam = searchParams.get("view")
    if (viewMode === "browse") {
      if (!currentViewParam) return
      const next = new URLSearchParams(searchParams.toString())
      next.delete("view")
      next.delete("tab")
      next.delete("editor")
      const query = next.toString()
      router.replace(query ? `/?${query}` : "/", { scroll: false })
      return
    }
    if (currentViewParam === viewMode) return
    const next = new URLSearchParams(searchParams.toString())
    next.set("view", viewMode)
    router.replace(`/?${next.toString()}`, { scroll: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode])

  // Restore the open post from the `?post=` URL param on mount — kept as a
  // separate effect (not a useState lazy initializer) because sessionStorage
  // isn't available during SSR; doing this in the initializer would render
  // "closed" on the server and "open" on the first client pass, a hydration
  // mismatch. useSearchParams() itself IS SSR-safe (see viewParam above), the
  // storage lookup behind it isn't. Runs once — after this, the write-side
  // effect below owns keeping the URL in sync with selectedPost.
  useEffect(() => {
    const key = searchParams.get("post")
    if (!key) return
    const restored = readSelectedPostFromSession(key)
    if (restored) {
      setSelectedPost(restored.post)
      if (restored.vr) setVRMode(true)
    }
    // Nothing to restore (different tab/device, or storage was cleared) —
    // clear the stale param.
    else setSelectedPostUrlParam(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Same fix, same shape, for the post detail modal — and, deliberately,
  // for VR mode too (NOT "for free": checked live that lib/vr-store.ts's
  // persist config excludes isVRMode from what it saves, so it does not
  // survive a refresh on its own — this cache is what actually restores it,
  // see the SelectedPostCache comment above). Caches the full post object on
  // open so a refresh can restore it without needing a universal "fetch a
  // booru post by id" (most sources don't support that). Only ever WRITES
  // (open or update) — closing is always the explicit closeSelectedPost()
  // above, never inferred here from selectedPost going null. An earlier
  // version tried to infer "closed" here and intermittently cleared the URL
  // mid-restore: the restore effect's setVRMode (a zustand store update) and
  // its setSelectedPost (a batched React update) don't necessarily land in
  // the same render pass, so this effect could observe selectedPost as still
  // null on a pass after the one-shot mount guard had already been used.
  useEffect(() => {
    if (!selectedPost) return
    const key = selectedPostSessionKey(selectedPost)
    try {
      const cache: SelectedPostCache = { post: selectedPost, vr: isVRMode }
      sessionStorage.setItem(`goonru:selected-post:${key}`, JSON.stringify(cache))
    } catch {
      // Storage full/unavailable (private mode, etc.) — the modal still
      // works for this session, it just won't survive a refresh.
    }
    setSelectedPostUrlParam(key)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPost, isVRMode])

  useEffect(() => {
    if ((bountySelectionActive || commissionSelectionActive) && viewMode !== "browse") {
      handleViewModeChange("browse")
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bountySelectionActive, commissionSelectionActive])

  // Auth dialog state
  const [authDialogOpen, setAuthDialogOpen] = useState(false)
  const [authBooruId, setAuthBooruId] = useState<string | null>(null)
  const { getSource } = useSourceStore()
  // The compact site picker used to be a hardcoded list of 10 (no Sakugabooru, Hitomi, Rule34.xyz,
  // Civitai...). Same rule as the Filters panel: whatever the user has enabled in the source store.
  const storeSources = useSourceStore((st) => st.sources)
  const miniSearchSites = useMemo(
    () => storeSources.filter((src) => src.enabled && !src.disabled).map((src) => src.id as BooruSite),
    [storeSources],
  )
  // A source that has since been marked out of service (e.g. Sakugabooru) must not stay selected from an earlier visit:
  // it would be searched for nothing and leave an empty grid.
  useEffect(() => {
    const outOfService = new Set(storeSources.filter((src) => src.disabled).map((src) => src.id))
    if (outOfService.size === 0 || !selectedSites.some((id) => outOfService.has(id))) return
    setSelectedSites(selectedSites.filter((id) => !outOfService.has(id)))
  }, [storeSources, selectedSites, setSelectedSites])
  const { getCredentials } = useBooruCredentialsStore()

  // First-time user filter button glow — shows once per account, ever.
  // Account-scoped (see lib/client/seen-state.ts) so it doesn't re-trigger on
  // a different browser/device; logged-out visitors fall back to the old
  // localStorage key since there's no account yet.
  const { user: filterGlowUser, isLoading: filterGlowAuthLoading } = useAuthStore()
  const [showFilterGlow, setShowFilterGlow] = useState(false)
  useEffect(() => {
    if (filterGlowAuthLoading) return
    const NUDGE_KEY = "nudge:filter_glow"
    const LOCAL_NUDGE_KEY = "goonru:has_opened_filters"
    let cancelled = false
    const run = async () => {
      let hasOpened: boolean
      if (filterGlowUser) {
        const { keys } = await fetchSeenState()
        hasOpened = keys.has(NUDGE_KEY)
        if (!hasOpened) void markSeen(NUDGE_KEY)
      } else {
        hasOpened = !!localStorage.getItem(LOCAL_NUDGE_KEY)
        if (!hasOpened) localStorage.setItem(LOCAL_NUDGE_KEY, "1")
      }
      if (cancelled || hasOpened) return
      setShowFilterGlow(true)
      setTimeout(() => { if (!cancelled) setShowFilterGlow(false) }, 6000)
    }
    void run()
    return () => { cancelled = true }
  }, [filterGlowAuthLoading, filterGlowUser])

  // First visit on a phone: open with just the welcome panel and a "pull down" hint; pulling reveals the toolbar and
  // then points at Filters. Shown once per device (or per account when logged in).
  // The pull-down variant (toolbar tucked away until pulled) is parked: flip this on to bring it back.
  const PULL_DOWN_INTRO_ENABLED = false
  const [introPhase, setIntroPhase] = useState<"off" | "collapsed" | "spotlight">("off")
  useEffect(() => {
    if (filterGlowAuthLoading) return
    if (!window.matchMedia("(max-width: 639px)").matches) return
    if (selectedSites.length > 0) return
    const INTRO_KEY = "nudge:mobile_intro"
    const LOCAL_INTRO_KEY = "goonru:mobile_intro_seen"
    let cancelled = false
    const run = async () => {
      let seen: boolean
      if (filterGlowUser) {
        const { keys } = await fetchSeenState()
        seen = keys.has(INTRO_KEY)
      } else {
        seen = !!localStorage.getItem(LOCAL_INTRO_KEY)
      }
      if (cancelled || seen) return
      if (PULL_DOWN_INTRO_ENABLED) {
        setIntroPhase("collapsed")
      } else {
        // Toolbar stays visible: go straight to pointing at Filters, once.
        setIntroPhase("spotlight")
        if (filterGlowUser) void markSeen(INTRO_KEY)
        else localStorage.setItem(LOCAL_INTRO_KEY, "1")
      }
    }
    void run()
    return () => {
      cancelled = true
    }
    // Decide once per load: later source choices must not restart or cancel it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterGlowAuthLoading, filterGlowUser])

  const revealIntro = useCallback(() => {
    setIntroPhase("spotlight")
    if (filterGlowUser) void markSeen("nudge:mobile_intro")
    else localStorage.setItem("goonru:mobile_intro_seen", "1")
  }, [filterGlowUser])
  const endIntro = useCallback(() => setIntroPhase("off"), [])

  const {
    sets,
    activeSetId,
    groups,
    activeGroupId,
    createSet,
    removeSet,
    updateSet,
    setActiveSet,
    renameSet,
    addNegativeTagToSet,
    removeNegativeTagFromSet,
    setActiveGroup,
    createGroup,
    deleteGroup,
    renameGroup,
    reorderSetsInGroup,
    moveSetToGroup,
    focus: tagFocus,
  } = useTagSetStore()

  const {
    handMode,
    showButtonLabels,
    hideScrollbar,
    shuffleMode,
    showScrollButtons,
    floatingButtonsAutoHide,
    floatingButtonsAutoHideDelay,
    searchBarPosition,
    filterAIImages,
    setFilterAIImages,
    stickyHeader,
    setStickyHeader,
  } = useSettingsStore()

  const { addToHistory, goBack, canGoBack } = useTagHistoryStore()

  // Auto-fade floating buttons
  const [buttonsVisible, setButtonsVisible] = useState(true)
  const fadeTimerRef = useRef<NodeJS.Timeout | null>(null)

  const resetFadeTimer = useCallback(() => {
    setButtonsVisible(true)
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current)
    if (floatingButtonsAutoHide && floatingButtonsAutoHideDelay > 0) {
      fadeTimerRef.current = setTimeout(() => {
        setButtonsVisible(false)
      }, floatingButtonsAutoHideDelay * 1000)
    }
  }, [floatingButtonsAutoHide, floatingButtonsAutoHideDelay])

  useEffect(() => {
    if (!floatingButtonsAutoHide || floatingButtonsAutoHideDelay === 0) {
      setButtonsVisible(true)
      return
    }
    resetFadeTimer()
    // Reset on scroll
    const handleScroll = () => resetFadeTimer()
    const container = mainRef.current
    container?.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      container?.removeEventListener("scroll", handleScroll)
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current)
    }
  }, [floatingButtonsAutoHide, floatingButtonsAutoHideDelay, resetFadeTimer])

  const { saveViewState, getViewState, isReturningToBrowse, setReturningToBrowse } = useViewStateStore()

  const { isLinkingMode, pendingPost, returnToEditor, returnToTab, clearLinking } = useScriptLinkingStore()

  const { addTag: addSavedTag, incrementFrequency } = useSavedTagsStore()

  const activeSet = sets.find((s) => s.id === activeSetId)
  const searchTags = effectiveSetTags(activeSet, tagFocus)
  const negativeTags = activeSet?.negativeTags || []

  // Debug: Log when searchTags changes
  useEffect(() => {
    console.log('[v0] searchTags changed', { searchTags, activeSetId, activeSet, sets })
    
    // Safety check: if activeSetId doesn't match any set, reset to first set
    if (activeSetId && !activeSet && sets.length > 0) {
      console.log('[v0] FIXING: activeSetId points to non-existent set, resetting to first set')
      setActiveSet(sets[0].id)
    }
  }, [searchTags, activeSetId, activeSet, sets, setActiveSet])

  // Load Rule34 tag categories on app startup (with 24-hour caching)
  useEffect(() => {
    const loadTagCategories = async () => {
      try {
        const { loadRule34TagCategories } = await import('@/lib/rule34-tag-scraper')
        await loadRule34TagCategories()
      } catch (error) {
        console.error('[v0] Error loading Rule34 tag categories:', error)
      }
    }
    
    loadTagCategories()
  }, []) // Run once on mount

  useEffect(() => {
    if (!activeSetId && sets.length > 0) {
      setActiveSet(sets[0].id)
    }
  }, [activeSetId, sets, setActiveSet])

  useEffect(() => {
    if (searchTags.length > 0) {
      addToHistory(searchTags)
    }
  }, [searchTags, addToHistory])

  // Remember the search with its source for the welcome panel's Recent list (this device only).
  const recordRecentBrowse = useRecentBrowseStore((state) => state.record)
  useEffect(() => {
    if (searchTags.length > 0 && selectedSites.length > 0) {
      recordRecentBrowse(selectedSites[0], searchTags)
    }
  }, [searchTags, selectedSites, recordRecentBrowse])

  const handleTagsChange = (newTags: string[]) => {
    console.log('[v0] handleTagsChange called', { newTags, activeSetId, currentTags: searchTags })
    if (activeSetId) {
      console.log('[v0] Calling updateSet from handleTagsChange', { activeSetId, newTags })
      updateSet(activeSetId, newTags)
      
      // Auto-switch to relevance sorting when searching with tags
      if (newTags.length > 0 && sortBy !== "relevance") {
        setSortBy("relevance")
      }
    } else {
      console.log('[v0] handleTagsChange skipped - no activeSetId')
    }
  }

  const handleNegativeTagsChange = (newNegativeTags: string[]) => {
    console.log('[v0] handleNegativeTagsChange called', { newNegativeTags, activeSetId, searchTags })
    if (activeSetId) {
      console.log('[v0] Calling updateSet from handleNegativeTagsChange', { activeSetId, searchTags, newNegativeTags })
      updateSet(activeSetId, searchTags, newNegativeTags)
    } else {
      console.log('[v0] handleNegativeTagsChange skipped - no activeSetId')
    }
  }

  const handleTagAdd = (tag: string) => {
    console.log('[v0] handleTagAdd called', { tag, activeSetId, searchTags, hasActiveSet: !!activeSet })
    if (activeSetId && !searchTags.includes(tag)) {
      console.log('[v0] Adding tag to set', { activeSetId, newTags: [...searchTags, tag] })
      const newTags = [...searchTags, tag]
      updateSet(activeSetId, newTags)
      
      // Auto-switch to relevance sorting when adding tags
      if (sortBy !== "relevance") {
        setSortBy("relevance")
      }
    } else {
      console.log('[v0] Tag add skipped', { 
        reason: !activeSetId ? 'no activeSetId' : 'tag already in searchTags',
        activeSetId,
        tagAlreadyExists: searchTags.includes(tag)
      })
    }
  }

  // Several tags at once (Find Similar). Calling handleTagAdd in a loop would build every new list from the same old
  // one, so only the last tag would stick.
  const handleTagsAdd = (tags: string[]) => {
    if (!activeSetId) return
    const fresh = tags.filter((tag, i) => !searchTags.includes(tag) && tags.indexOf(tag) === i)
    if (fresh.length === 0) return
    updateSet(activeSetId, [...searchTags, ...fresh])
    if (sortBy !== "relevance") {
      setSortBy("relevance")
    }
  }

  const handleNegativeTagAdd = (tag: string) => {
    if (activeSetId && !negativeTags.includes(tag)) {
      addNegativeTagToSet(activeSetId, tag)
    }
  }

  // Following feed toggle
  const [followingOnly, setFollowingOnly] = useState(false)
  const { following } = useUserProfileStore()
  // Show badge dot on the toggle when user follows someone and isn't in following mode
  const followingCount = following.length

  const handleFollowingToggle = useCallback(() => {
    setFollowingOnly((prev) => !prev)
  }, [])

  const handlePostsChange = useCallback((posts: BooruPost[]) => {
    setAllPosts(posts)
  }, [])

  const handlePostClick = useCallback(
    (post: BooruPost) => {
      // EpornerVR can't reliably hand back a directly-playable file server-side
      // (see searchEporner in lib/booru-api.tsx) — route into the VR page's
      // browser tab instead, where a real browser resolves it live, rather
      // than opening the normal in-modal player against a post with no
      // real fileUrl.
      if (post.site === "eporner-vr" && post.rawPost?.redirectToVRBrowser && post.source) {
        router.push(`/vr?loadUrl=${encodeURIComponent(post.source)}`)
        return
      }
      setSelectedPost(post)
      const index = allPosts.findIndex((p) => p.id === post.id)
      setSelectedPostIndex(index)
      trackTagAffinity(post.tags)
    },
    [allPosts, router],
  )

  const handleNavigate = useCallback(
    (direction: "prev" | "next") => {
      if (allPosts.length === 0 || selectedPostIndex === -1) return

      const newIndex = direction === "next" ? selectedPostIndex + 1 : selectedPostIndex - 1

      if (newIndex >= 0 && newIndex < allPosts.length) {
        setSelectedPost(allPosts[newIndex])
        setSelectedPostIndex(newIndex)
        trackTagAffinity(allPosts[newIndex].tags)
      }
    },
    [allPosts, selectedPostIndex],
  )

  const canNavigate = {
    prev: selectedPostIndex > 0,
    next: selectedPostIndex >= 0 && selectedPostIndex < allPosts.length - 1,
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart({ x: e.targetTouches[0].clientX, y: e.targetTouches[0].clientY })
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd({ x: e.targetTouches[0].clientX, y: e.targetTouches[0].clientY })
  }

  const handleTouchEnd = () => {
    const deltaX = touchStart.x - touchEnd.x

    const hideDirection = handMode === "right" ? "right" : "left"

    if (hideDirection === "right" && deltaX < -100) {
      setHideUI(!hideUI)
      return
    }
    if (hideDirection === "left" && deltaX > 100) {
      setHideUI(!hideUI)
      return
    }
  }

  const sheetSide = handMode === "right" ? "right" : "left"

  const scrollToTop = () => {
    console.log("[v0] Scroll to top clicked")
    mainRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  const scrollToBottom = () => {
    console.log("[v0] Scroll to bottom clicked")
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: mainRef.current.scrollHeight, behavior: "smooth" })
    }
  }

  const handleGoBack = () => {
    const previousTags = goBack()
    if (previousTags && activeSetId) {
      updateSet(activeSetId, previousTags)
    }
  }

  // A tag opened from the context menu becomes its own search. If a search with exactly those tags already exists, use it
  // rather than adding a duplicate.
  const findSetWithTags = (tags: string[]) =>
    sets.find((s) => s.tags.length === tags.length && tags.every((t) => s.tags.includes(t)))

  const handleOpenTagInNewSet = (tag: string) => {
    const tags = tag.includes("+") ? tag.split("+").filter((t) => t.trim()) : [tag]
    const existing = findSetWithTags(tags)
    if (existing) setActiveSet(existing.id)
    else createSet(tags)
    closeSelectedPost()
  }

  // Saves the tag as its own search WITHOUT leaving the current one, so the user can keep browsing the post.
  const handleOpenTagInBackgroundSet = (tag: string) => {
    if (!findSetWithTags([tag])) createSet([tag], undefined, false)
  }


  // Mini search bar handlers
  const handleToggleMiniSearch = () => {
    setShowMiniSearch(!showMiniSearch)

    if (!showMiniSearch) {
      setHideUI(true)
    } else {
      setHideUI(false)
    }
  }

  const handleSearchConsoleTagSelect = (tag: string) => {
    if (activeSetId && !searchTags.includes(tag)) {
      const updatedTags = [...searchTags, tag]
      updateSet(activeSetId, updatedTags)
      addSavedTag(tag)
      incrementFrequency(tag)
    }
  }

  const handleSearchConsoleMultiTagSelect = (tags: string[]) => {
    if (activeSetId) {
      const newTags = tags.filter(tag => !searchTags.includes(tag))
      if (newTags.length > 0) {
        const updatedTags = [...searchTags, ...newTags]
        updateSet(activeSetId, updatedTags)
        newTags.forEach(tag => {
          addSavedTag(tag)
          incrementFrequency(tag)
        })
      }
    }
  }

  const handleMiniSearchTagsChange = (newTags: string[]) => {
    if (activeSetId) {
      updateSet(activeSetId, newTags)
      // Save new tags
      newTags.forEach(tag => {
        if (!searchTags.includes(tag)) {
          addSavedTag(tag)
          incrementFrequency(tag)
        }
      })
    }
  }

  const handleMiniSearchCreateSet = (tags: string[]) => {
    const newSetId = createSet(tags)
    tags.forEach(tag => {
      addSavedTag(tag)
      incrementFrequency(tag)
    })
    return newSetId
  }

  // The source filter bar sticks directly beneath this header, and the header's
  // height isn't constant (linking banner, tag-set row, wrapping on narrow
  // screens), so publish the measured height instead of hardcoding an offset.
  useEffect(() => {
    const el = browseHeaderRef.current
    if (!el) return
    const publish = () => {
      const h = stickyHeader && !hideUI && introPhase !== "collapsed" ? el.getBoundingClientRect().height : 0
      document.documentElement.style.setProperty("--browse-header-h", `${Math.round(h)}px`)
    }
    publish()
    const ro = new ResizeObserver(publish)
    ro.observe(el)
    return () => ro.disconnect()
  }, [stickyHeader, hideUI, viewMode, introPhase])

  const handleViewModeChange = (newMode: "browse" | "collections" | "editors" | "gooncave" | "game-arena") => {
    const targetMode = viewMode === newMode ? "browse" : newMode

    console.log("[v0] View mode changing from", viewMode, "to", targetMode)

    if (viewMode === "browse" && targetMode !== "browse" && mainRef.current) {
      const scrollPos = mainRef.current.scrollTop
      console.log("[v0] Saving browse state - scrollTop:", scrollPos, "activeSetId:", activeSetId)
      saveViewState(scrollPos, activeSetId)
    }

    if (viewMode !== "browse" && targetMode === "browse") {
      setReturningToBrowse(true)
      if (searchParams.get("tab") === "scripter" || searchParams.get("editor")) {
        router.replace("/", { scroll: false })
      }
    }

    prevViewModeRef.current = viewMode
    setViewMode(targetMode)
  }

  useEffect(() => {
    if (viewMode === "browse" && isReturningToBrowse && mainRef.current) {
      const { scrollPosition, activeSetId: savedSetId } = getViewState()
      console.log("[v0] Restoring browse state - scrollPosition:", scrollPosition, "savedSetId:", savedSetId)

      if (savedSetId && savedSetId !== activeSetId) {
        setActiveSet(savedSetId)
      }

      const restoreTimer = setTimeout(() => {
        if (mainRef.current && scrollPosition > 0) {
          console.log("[v0] Setting scroll position to:", scrollPosition)
          mainRef.current.scrollTop = scrollPosition
        }
        setReturningToBrowse(false)
      }, 150)

      return () => clearTimeout(restoreTimer)
    }
  }, [viewMode, isReturningToBrowse])

  useEffect(() => {
    console.log(
      "[v0] Effect 1 check: returnToEditor:",
      returnToEditor,
      "viewMode:",
      viewMode,
      "returnToTab:",
      returnToTab,
    )
    if (returnToEditor && (viewMode === "browse" || viewMode === "collections")) {
      // Gooncave is a return destination like the editors are, but it lives in its own
      // view mode — routing it to "editors" is what stranded slot-fills in the Editors Hub.
      if (returnToTab === "Gooncave") {
        setViewMode("gooncave")
        return
      }
      console.log("[v0] Effect 1: Returning to editors, returnToTab:", returnToTab)
      setViewMode("editors")
      if (!pendingPost && returnToTab === "global-script-player") {
        useScriptLinkingStore.setState({ returnToEditor: false })
      }
    }
  }, [pendingPost, returnToEditor, viewMode, returnToTab])

  // Handle notification deep-links: ?tab=commissions&commission=ID or ?tab=bounties or ?tab=community
  useEffect(() => {
    const tab = searchParams.get("tab")
    if (tab === "commissions" || tab === "bounties") {
      setViewMode("editors")
      useScriptLinkingStore.setState({ returnToEditor: true, returnToTab: tab })
    }
    if (tab === "community") {
      setViewMode("editors")
    }
    if (searchParams.get("bounty_payment") === "success") {
      toast.success(tt("bountyFunded"), { description: tt("escrowIsHeldAndTheWorkCanBegin") })
    }
    if (searchParams.get("commission_paid") === "success") {
      toast.success(tt("commissionPaid"), { description: tt("escrowIsHeldAndTheWorkCanBegin") })
    }
  // Only run on initial mount / URL change, not on every state update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  // Cancel linking mode only when the user *navigates back* to editors (prev !== editors),
  // not when they initiate linking from within editors (prev === editors).
  useEffect(() => {
    if (viewMode === "editors" && isLinkingMode && prevViewModeRef.current !== "editors") {
      clearLinking()
    }
  }, [viewMode, isLinkingMode, clearLinking])

  // Keep prevViewModeRef in sync with every viewMode change, not just ones routed through
  // handleViewModeChange — startLinking() (e.g. Playmate's "Search Booru") flips isLinkingMode
  // while viewMode is already "editors" and never calls handleViewModeChange, so without this
  // the ref stays stuck at whatever mode preceded the *first* arrival at editors, permanently
  // failing the "!== editors" check above and clearing linking mode the instant it's set.
  useEffect(() => {
    prevViewModeRef.current = viewMode
  }, [viewMode])

  useEffect(() => {
    // "collections" (Goonfolio/Favorites) is a valid place to be while linking too — e.g. the
    // "Search Goonfolio" option in an editor's Add Content dialog routes here on purpose instead
    // of the booru browse grid. Only force back to "browse" for anything else (editors, etc).
    if (isLinkingMode && viewMode !== "browse" && viewMode !== "collections" && !returnToEditor) {
      setViewMode("browse")
    }
  }, [isLinkingMode, viewMode, returnToEditor])

  // Clear a stale booru post modal only when *transitioning into* editors (e.g. the
  // user had a post open in Browse, then switched tabs) — not every time selectedPost
  // changes while already in editors, which would instantly close a post opened via
  // "Open Content" from within a bounty/commission (OrderContentViewer's booruPost button).
  const prevViewModeForSelectedPostRef = useRef(viewMode)
  useEffect(() => {
    const prevViewMode = prevViewModeForSelectedPostRef.current
    prevViewModeForSelectedPostRef.current = viewMode
    if (viewMode === "editors" && prevViewMode !== "editors" && selectedPost) {
      closeSelectedPost()
    }
  }, [viewMode, selectedPost])

  useEffect(() => {
    // Don't auto-select all enabled sources - let user choose which sources to search
    // This prevents unnecessary searches to multiple sources when user only wants one
    console.log("[v0] App initialized - user must select sources to search")
  }, [])

  // Capture referral code from URL and store in localStorage
  useEffect(() => {
    const refCode = searchParams.get('ref')
    if (refCode) {
      localStorage.setItem('goonru_referral_code', refCode)
      console.log('[Referral] Stored referral code:', refCode)
    }
  }, [searchParams])

  // Handle direct script deep-link: /?script=ID (from "Copy Link" on a script)
  useEffect(() => {
    const scriptId = searchParams.get('script')
    if (!scriptId) return
    useMarketplaceStore.getState().setPendingOpenScript({ scriptId, showPurchaseToast: false })
    setViewMode('editors')
    router.replace('/?tab=community', { scroll: false })
  }, [searchParams, router])

  // Handle post-purchase redirect: fulfill purchase then show a toast; "Play it now" opens the modal.
  useEffect(() => {
    if (searchParams.get('purchase') !== 'success') return
    const sessionId = searchParams.get('session_id')
    const scriptId = searchParams.get('script_id')
    const postParam = searchParams.get('post')

    // Only force the Editors Hub/marketplace view when the user wasn't
    // looking at a booru post when they bought (i.e. they bought from the
    // marketplace tab itself, the normal case this redirect was written
    // for). If `post` is present they trialed+bought a script from inside
    // the booru detail modal — leave viewMode/post alone so the restore
    // effect above puts them back on the exact content they were viewing
    // instead of yanking them into Editors Hub.
    if (!postParam) {
      setViewMode('editors')
      router.replace('/?tab=community', { scroll: false })
    } else {
      const next = new URLSearchParams(searchParams.toString())
      next.delete('purchase')
      next.delete('session_id')
      next.delete('script_id')
      const query = next.toString()
      router.replace(query ? `/?${query}` : '/', { scroll: false })
    }

    if (!sessionId) return

    fetch('/api/marketplace/fulfill-purchase', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then(r => r.json())
      .then(data => {
        // Cart purchase returns scriptIds (array); single purchase returns scriptId
        const firstScriptId = scriptId ?? data.scriptId ?? data.scriptIds?.[0]
        const isCart = Array.isArray(data.scriptIds) && data.scriptIds.length > 1
        toast.success(tt("purchaseComplete"), {
          description: isCart
            ? tt("lengthScriptsAddedToYourLibrary", { length: data.scriptIds.length })
            : tt("yourScriptIsReadyToPlay"),
          duration: 10000,
          action: firstScriptId ? {
            label: 'Play it now →',
            onClick: () => {
              useMarketplaceStore.getState().setPendingOpenScript({ scriptId: firstScriptId, showPurchaseToast: false })
            },
          } : undefined,
        })
      })
      .catch(() => {
        toast.error(tt("purchaseMayHaveFailed"), { description: tt("checkYourLibraryOrContactSupport") })
      })
  }, [searchParams, router])

  // Handle VisiSearch tags from URL params
  useEffect(() => {
    const tagsParam = searchParams.get('tags')
    if (tagsParam && activeSetId) {
      const newTags = tagsParam.split('+').filter(tag => tag.trim())
      if (newTags.length > 0) {
        console.log('[VisiSearch] Loading tags from URL:', newTags)
        updateSet(activeSetId, newTags)
        
        // Save tags to saved tags store
        newTags.forEach(tag => {
          addSavedTag(tag)
          incrementFrequency(tag)
        })
        
        // Clear URL params after loading
        router.replace('/', { scroll: false })
      }
    }
  }, [searchParams, activeSetId, updateSet, addSavedTag, incrementFrequency, router])

  // Listen for auth dialog open events
  useEffect(() => {
    const handleOpenAuth = (event: CustomEvent) => {
      const { booruId } = event.detail
      setAuthBooruId(booruId)
      setAuthDialogOpen(true)
    }

    window.addEventListener('open-booru-auth' as any, handleOpenAuth)
    return () => window.removeEventListener('open-booru-auth' as any, handleOpenAuth)
  }, [])

  useEffect(() => {
    const handleOpenBooruPost = (event: CustomEvent<BooruPost>) => {
      if (!event.detail?.id) return
      setSelectedPost(event.detail)
    }

    window.addEventListener("open-booru-post" as any, handleOpenBooruPost as EventListener)
    return () => window.removeEventListener("open-booru-post" as any, handleOpenBooruPost as EventListener)
  }, [])

  // Handle saving credentials
  const handleSaveCredentials = async (credentials: { username?: string; apiKey?: string }) => {
    if (!authBooruId) return

    try {
      const response = await fetch('/api/booru/credentials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booruId: authBooruId,
          username: credentials.username,
          apiKey: credentials.apiKey,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        console.error('[v0] Failed to save credentials:', data)
        throw new Error(data.error || 'Failed to save credentials')
      }

      toast.success(tt("credentialsSavedSuccessfully"))
      
      // Optionally refresh the search to use new credentials
      // You could trigger a re-search here if needed
    } catch (error: any) {
      console.error('[v0] Error saving credentials:', error)
      toast.error(error.message || tt("failedToSaveCredentials"))
      throw error
    }
  }

  const authSource = authBooruId ? getSource(authBooruId) : null

  return (
    <div className="flex flex-col h-screen">
      {hideUI && (
        <Button
          variant="ghost"
          size="icon"
          className="fixed top-4 right-4 h-10 w-10 rounded-full glass shadow-lg z-50 hover:bg-accent/50"
          onClick={() => setHideUI(false)}
        >
          <Eye className="h-4 w-4" />
        </Button>
      )}

      <div
        ref={mainRef}
        className={cn(
          "flex-1 overflow-x-hidden",
          // "editors" (Editors Hub — Playmate, Script Player, My Scripts, ...)
          // is self-contained the same way game-arena is: its own
          // root manages full-height layout and each tab scrolls its own
          // content internally. Leaving it on the generic overflow-y-auto
          // branch let THIS wrapper become the scrollable element instead,
          // scrolling the toolbar/tabs away with everything else and
          // defeating every internal height/overflow containment downstream
          // (e.g. Playmate's chat input ending up below the fold).
          viewMode === "gooncave" || viewMode === "game-arena" || viewMode === "editors" ? "overflow-hidden flex flex-col min-h-0" : "overflow-y-auto",
          hideScrollbar ? "scrollbar-hide" : "",
          viewMode === "browse" ? "pb-11" : ""
        )}
      >
        {introPhase === "collapsed" && (
          <>
            <PullDownHint onReveal={revealIntro} />
            {/* keeps the welcome panel clear of the hint while the toolbar is tucked away */}
            <div aria-hidden="true" className="h-16" />
          </>
        )}
        {introPhase === "spotlight" && <FiltersBubble targetId="toolbar-filters-button" onDismiss={endIntro} />}
        <header
          ref={browseHeaderRef}
          className={cn("z-[45] shadow-sm", hideUI || introPhase === "collapsed" ? "hidden" : "", introPhase === "spotlight" && PULL_DOWN_INTRO_ENABLED && "animate-[header-slide-down_0.45s_ease-out]", stickyHeader ? "sticky top-0" : "relative")}
        >
          {/* Linking mode banner — cancel button */}
          {isLinkingMode && (
            <div className="relative z-10 bg-background border-b border-primary/30 px-4 py-2 flex items-center justify-between">
              <span className="text-sm font-medium text-primary">
                {returnToTab === "Gooncave"
                  ? tt("selectContentForGooncaveClickAPostTo")
                  : tt("selectContentForYourScriptClickAPostTo")}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs border-primary/30 text-primary hover:bg-primary/10"
                onClick={() => {
                  const wasGooncave = returnToTab === "Gooncave"
                  clearLinking()
                  handleViewModeChange(wasGooncave ? "gooncave" : "editors")
                }}
              >
                {tt("cancel")}
              </Button>
            </div>
          )}
          <div className={cn("border-b border-border/50", stickyHeader ? "backdrop-blur-xl bg-background/80" : "")}>
            {/* pb is 1.5 rather than 2 so the gap under the tag-set row matches the
                space-y-1.5 rhythm above it — the container bottom padding was the only
                thing making that last gap 8px while every other gap was 6px. */}
            <div className="container mx-auto px-3 pt-2 pb-1.5 space-y-1.5">
              <div className="flex items-center justify-center gap-2 max-[400px]:gap-1 max-[350px]:gap-0.5 overflow-x-auto scrollbar-hide px-1 w-full [&>*]:shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleViewModeChange("collections")}
                  title={tt("goonfolio")}
                  className={cn(
                    "h-8 w-8 sm:h-7 sm:w-7 max-[400px]:h-7 max-[400px]:w-7 shrink-0 rounded-full border border-border/50",
                    viewMode === "collections"
                      ? "bg-blue-500/60 hover:bg-blue-500/70"
                      : "bg-secondary/60 hover:bg-blue-500/50",
                  )}
                >
                  <Folder className="h-4 w-4 max-[400px]:h-4 max-[400px]:w-4" />
                </Button>
                {showButtonLabels && <span className="text-xs">{tt("collections")}</span>}

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleViewModeChange("editors")}
                  title={tt("editorsHub")}
                  className={cn(
                    "h-8 w-8 sm:h-7 sm:w-7 max-[400px]:h-7 max-[400px]:w-7 rounded-full border border-border/50",
                    viewMode === "editors"
                      ? "bg-pink-500/60 hover:bg-pink-500/70"
                      : "bg-secondary/60 hover:bg-pink-500/50",
                  )}
                >
                  <Mouse className="h-4 w-4 max-[400px]:h-4 max-[400px]:w-4" />
                </Button>
                {showButtonLabels && <span className="text-xs">{tt("editors")}</span>}

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleViewModeChange("game-arena")}
                  title={tt("gameArena")}
                  className={cn(
                    "h-8 w-8 sm:h-7 sm:w-7 max-[400px]:h-7 max-[400px]:w-7 rounded-full border border-border/50",
                    viewMode === "game-arena"
                      ? "bg-green-500/60 hover:bg-green-500/70"
                      : "bg-secondary/60 hover:bg-green-500/50",
                  )}
                >
                  <Gamepad2 className="h-4 w-4 max-[400px]:h-4 max-[400px]:w-4" />
                </Button>
                {showButtonLabels && <span className="text-xs">{tt("games")}</span>}

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleViewModeChange("gooncave")}
                  title={tt("gooncave")}
                  className={cn(
                    "h-8 w-8 sm:h-7 sm:w-7 max-[400px]:h-7 max-[400px]:w-7 rounded-full border border-border/50",
                    viewMode === "gooncave"
                      ? "bg-purple-500/60 hover:bg-purple-500/70"
                      : "bg-secondary/60 hover:bg-purple-500/50",
                  )}
                >
                  <HugeiconsIcon icon={LayoutDashboardIcon} strokeWidth={2} className="h-4 w-4 max-[400px]:h-4 max-[400px]:w-4" />
                </Button>
                {showButtonLabels && <span className="text-xs">{tt("gooncave")}</span>}

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => router.push("/vr")}
                  title={tt("vRPlayer")}
                  className="h-8 w-8 sm:h-7 sm:w-7 max-[400px]:h-7 max-[400px]:w-7 rounded-full border border-border/50 bg-secondary/60 hover:bg-yellow-500/60"
                >
                  <Visor className="h-4 w-4 max-[400px]:h-4 max-[400px]:w-4" />
                </Button>
                {showButtonLabels && <span className="text-xs">VR</span>}

                <FeatureFlagWrapper featureName="goonpals">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => router.push("/goonpals")}
                    title={tt("goonpalsAICompanions")}
                    className="h-8 w-8 sm:h-7 sm:w-7 max-[400px]:h-7 max-[400px]:w-7 rounded-full border border-border/50 bg-secondary/60 hover:bg-cyan-500/60"
                  >
                    <HugeiconsIcon icon={Alien02Icon} strokeWidth={2} className="h-4 w-4 max-[400px]:h-4 max-[400px]:w-4" />
                  </Button>
                  {showButtonLabels && <span className="text-xs">{tt("goonpals")}</span>}
                </FeatureFlagWrapper>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => router.push("/commissions")}
                  title={tt("commissionsRequestOrOfferCustomArt")}
                  className="h-8 w-8 sm:h-7 sm:w-7 max-[400px]:h-7 max-[400px]:w-7 rounded-full border border-border/50 bg-secondary/60 hover:bg-orange-500/60"
                >
                  <Palette className="h-4 w-4 max-[400px]:h-4 max-[400px]:w-4" />
                </Button>
                {showButtonLabels && <span className="text-xs">{tt("commissions")}</span>}

                <Sheet>
                  <SheetTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      title={tt("settings")}
                      className="h-8 w-8 sm:h-7 sm:w-7 max-[400px]:h-7 max-[400px]:w-7 rounded-full hover:bg-slate-500/50 bg-secondary/60 border border-border/50"
                    >
                      <Settings className="h-4 w-4 max-[400px]:h-4 max-[400px]:w-4" />
                    </Button>
                  </SheetTrigger>
                  {showButtonLabels && <span className="text-xs">{tt("settings")}</span>}
                  <SheetContent
                    side={sheetSide}
                    showCloseButton={false}
                    className="w-80 overflow-y-auto !bg-card/60 backdrop-blur-2xl backdrop-saturate-150 border-l border-border/30 px-2"
                  >
                    <SheetHeader className="p-0">
                      <SheetTitle className="sr-only">{tt("settings")}</SheetTitle>
                    </SheetHeader>
                    <div className="space-y-4 pt-2">
                      <div className="flex items-center justify-between px-1">
                        <div className="flex items-center gap-2">
                          <Settings className="h-4 w-4 text-muted-foreground" />
                          <h2 className="font-semibold text-sm">{tt("settings")}</h2>
                        </div>
                        <SheetClose asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full hover:bg-muted">
                            <X className="h-4 w-4" />
                            <span className="sr-only">{tt("close")}</span>
                          </Button>
                        </SheetClose>
                      </div>
                      <div className="flex items-center justify-between gap-3 px-2.5 py-2 rounded-lg bg-card border border-border">
                        <div className="flex items-center gap-2">
                          <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <span className="text-xs font-medium">{tt("language")}</span>
                        </div>
                        <LanguageSelector className="h-7 w-28 text-xs" />
                      </div>
                      <SettingsPanel />
                    </div>
                  </SheetContent>
                </Sheet>

                <Sheet>
                  <SheetTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      id="toolbar-filters-button"
                      title={tt("filters")}
                      className={cn(
                        "h-8 w-8 sm:h-7 sm:w-7 max-[400px]:h-7 max-[400px]:w-7 rounded-full hover:bg-cyan-500/50 bg-secondary/60 border border-border/50",
                        ((showFilterGlow && introPhase === "off") || introPhase === "spotlight") && "filter-glow-pulse"
                      )}
                    >
                      <Sliders className="h-4 w-4 max-[400px]:h-4 max-[400px]:w-4" />
                    </Button>
                  </SheetTrigger>
                  {showButtonLabels && <span className="text-xs">{tt("filters")}</span>}
                  <SheetContent
                    side={sheetSide}
                    showCloseButton={false}
                    className="w-80 overflow-y-auto !bg-card/60 backdrop-blur-2xl backdrop-saturate-150 border-l border-border/30 px-2 py-2"
                  >
                    <SheetHeader className="p-0">
                      <SheetTitle className="sr-only">{tt("filters")}</SheetTitle>
                    </SheetHeader>
                    <div className="pl-0 space-y-1 pt-1">
                      <div className="flex items-center justify-between px-1 pb-1">
                        <div className="flex items-center gap-2">
                          <Filter className="h-4 w-4 text-muted-foreground" />
                          <h2 className="font-semibold text-sm">{tt("filters")}</h2>
                        </div>
                        <SheetClose asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full hover:bg-muted">
                            <X className="h-4 w-4" />
                            <span className="sr-only">{tt("close")}</span>
                          </Button>
                        </SheetClose>
                      </div>
                      <FilterPanel
                        selectedSites={selectedSites}
                        onSitesChange={setSelectedSites}
                        activeRatings={activeRatings}
                        onActiveRatingsChange={setActiveRatings}
                        sortBy={sortBy}
                        onSortByChange={(next) => {
                          setSortBy(next)
                          if (next === "random") setRandomSeed(Date.now())
                        }}
                        onRandomizeSorting={() => setRandomSeed(Date.now())}
                        sortDirection={sortDirection}
                        onSortDirectionChange={setSortDirection}
                        dateRange={dateRange}
                        onDateRangeChange={setDateRange}
                        viewMode={viewMode}
                        onViewModeChange={handleViewModeChange}
                        filterAIImages={filterAIImages}
                        onFilterAIImagesChange={setFilterAIImages}
                        contentTypeVisibility={contentTypeVisibility}
                        onContentTypeVisibilityChange={setContentTypeVisibility}
                        gifsOnly={gifsOnly}
                        onGifsOnlyChange={setGifsOnly}
                        currentPosts={allPosts}
                        onTagAdd={handleTagAdd}
                      />
                    </div>
                  </SheetContent>
                </Sheet>

                <UserMenu />
                {showButtonLabels && <span className="text-xs">{tt("account")}</span>}

                {shuffleMode && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      title={tt("shuffle")}
                      className="h-9 w-9 max-[400px]:h-7 max-[400px]:w-7 rounded-full hover:bg-secondary/70 bg-secondary/60 border border-border/50"
                      onClick={() => {
                        const randomPage = Math.floor(Math.random() * 100)
                      }}
                    >
                      {shuffleMode ? <Dices className="h-4 w-4 max-[400px]:h-4 max-[400px]:w-4" /> : <Shuffle className="h-4 w-4 max-[400px]:h-4 max-[400px]:w-4" />}
                    </Button>
                    {showButtonLabels && <span className="text-xs">{tt("shuffle")}</span>}
                  </>
                )}
              </div>

              {searchBarPosition === "top" && viewMode === "browse" && (
                <>
                  <div className="flex items-center gap-1.5 max-w-full">
                    <div className="flex-1 min-w-0">
                      <SearchBar
                        tags={searchTags}
                        negativeTags={negativeTags}
                        onTagsChange={handleTagsChange}
                        onNegativeTagsChange={handleNegativeTagsChange}
                        placeholder={tt("searchByTags")}
                        activeSite={selectedSites[0] || "danbooru"}
                        trendingSites={selectedSites}
                        mobileEndSlot={canGoBack() ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleGoBack}
                            title={tt("goBackTagHistory")}
                            className="h-6 w-6 rounded-md hover:bg-secondary/70 bg-secondary/60 border border-border/50"
                          >
                            <ArrowLeft className="h-3 w-3" />
                          </Button>
                        ) : undefined}
                      />
                    </div>
                    {canGoBack() && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleGoBack}
                        title={tt("goBackTagHistory")}
                        className="hidden sm:flex rounded-lg hover:bg-secondary/70 bg-secondary/60 border border-border/50 shrink-0 w-[38px] h-[38px]"
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <TagSetManager
                    sets={sets}
                    activeSetId={activeSetId}
                    groups={groups}
                    activeGroupId={activeGroupId}
                    onSetChange={setActiveSet}
                    onSetCreate={createSet}
                    onSetRemove={removeSet}
                    onSetUpdate={updateSet}
                    onSetRename={renameSet}
                    onGroupChange={setActiveGroup}
                    onGroupCreate={createGroup}
                    onGroupDelete={deleteGroup}
                    onGroupRename={renameGroup}
                    onReorderSets={reorderSetsInGroup}
                    onMoveSetToGroup={moveSetToGroup}
                    currentTags={searchTags}
                    onTagsLoad={(tags, name) => {
                      // Check if a set with this name already exists
                      const existingSet = sets.find(s => s.name === name)
                      
                      if (existingSet) {
                        // Generate unique name
                        let counter = 1
                        let uniqueName = `${name}_${counter}`
                        while (sets.find(s => s.name === uniqueName)) {
                          counter++
                          uniqueName = `${name}_${counter}`
                        }
                        // Create new set with unique name
                        const newSetId = createSet(tags, uniqueName)
                        setActiveSet(newSetId)
                      } else {
                        // Create new set with original name
                        const newSetId = createSet(tags, name)
                        setActiveSet(newSetId)
                      }
                    }}
                    followingOnly={followingOnly}
                    followingCount={followingCount}
                    onFollowingToggle={handleFollowingToggle}
                  />
                </>
              )}
            </div>
          </div>
        </header>

        <main
          // Same reasoning as the wrapper above (line ~771) — these modes need
          // to actually participate in the flex-col height budget instead of
          // sizing to their content's intrinsic height, or a fixed-viewport-
          // height descendant (EditorsHub, Gooncave, ...) will always overshoot
          // by however much the header above it already took up. This list must
          // stay in sync with the wrapper's condition above.
          className={cn(viewMode === "game-arena" || viewMode === "editors" || viewMode === "gooncave" ? "flex-1 overflow-hidden min-h-0" : "")}
        >
          {bountySelectionActive && viewMode === "browse" && (
            <div className="sticky top-0 z-30 border-b border-primary/30 bg-primary/10 backdrop-blur px-3 py-2">
              <div className="container mx-auto flex items-center gap-3">
                <HeartHandshake className="h-4 w-4 text-primary shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{tt("selectContentForYourBounty")}</p>
                  <p className="text-xs text-muted-foreground">{tt("openAPostThenChooseAddToBounty")}</p>
                </div>
                <Button variant="outline" size="sm" onClick={cancelBountySelection}>
                  {tt("cancel")}
                </Button>
              </div>
            </div>
          )}

          {commissionSelectionActive && viewMode === "browse" && (
            <div className="sticky top-0 z-30 border-b border-primary/30 bg-primary/10 backdrop-blur px-3 py-2">
              <div className="container mx-auto flex items-center gap-3">
                <HeartHandshake className="h-4 w-4 text-primary shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{tt("selectContentForYourCommission")}</p>
                  <p className="text-xs text-muted-foreground">{tt("openAPostThenChooseAddToComm")}</p>
                </div>
                <Button variant="outline" size="sm" onClick={cancelCommissionSelection}>
                  {tt("cancel")}
                </Button>
              </div>
            </div>
          )}

          {viewMode === "browse" && followingOnly && (
            <FollowingFeed />
          )}

          {viewMode === "browse" && !followingOnly && (
            <BooruBrowser
              sites={selectedSites}
              tags={searchTags}
              negativeTags={negativeTags}
              activeRatings={activeRatings}
              sortBy={sortBy}
              sortDirection={sortDirection}
              randomSeed={randomSeed}
              dateRange={dateRange}
              onPostClick={handlePostClick}
              onPostsChange={handlePostsChange}
              onTagClick={handleTagAdd}
              contentTypeVisibility={contentTypeVisibility}
              onContentTypeVisibilityChange={setContentTypeVisibility}
              gifsOnly={gifsOnly}
              onGifsOnlyChange={setGifsOnly}
            />
          )}

          {viewMode === "collections" && (
            <LibraryBrowser
              onBack={() => handleViewModeChange("browse")}
              onOpenEditors={() => handleViewModeChange("editors")}
            />
          )}

          {viewMode === "editors" && <EditorsHub onViewModeChange={handleViewModeChange} />}

          {viewMode === "gooncave" && (
            <Gooncave
              onClose={() => handleViewModeChange("browse")}
              initialPosts={allPosts}
              onSearchGoonfolio={() => handleViewModeChange("collections")}
            />
          )}

          {viewMode === "game-arena" && <GameArena onClose={() => handleViewModeChange("browse")} />}
        </main>

        {searchBarPosition === "bottom" && !hideUI && viewMode === "browse" && (
          <div className={cn("glass-strong border-t border-border/50 sticky bottom-0 z-20 shadow-sm")}>
            <div className="container mx-auto px-3 py-2.5 space-y-2.5">
              <SearchBar
                tags={searchTags}
                negativeTags={negativeTags}
                onTagsChange={handleTagsChange}
                onNegativeTagsChange={handleNegativeTagsChange}
                placeholder={tt("searchByTags")}
                activeSite={selectedSites[0] || "danbooru"}
                trendingSites={selectedSites}
              />

              <TagSetManager
                sets={sets}
                activeSetId={activeSetId}
                groups={groups}
                activeGroupId={activeGroupId}
                onSetChange={setActiveSet}
                onSetCreate={createSet}
                onSetRemove={removeSet}
                onSetUpdate={updateSet}
                onSetRename={renameSet}
                onGroupChange={setActiveGroup}
                onGroupCreate={createGroup}
                onGroupDelete={deleteGroup}
                onGroupRename={renameGroup}
                onReorderSets={reorderSetsInGroup}
                onMoveSetToGroup={moveSetToGroup}
                currentTags={searchTags}
                onTagsLoad={(tags, name) => {
                  // Check if a set with this name already exists
                  const existingSet = sets.find(s => s.name === name)

                  if (existingSet) {
                    // Generate unique name
                    let counter = 1
                    let uniqueName = `${name}_${counter}`
                    while (sets.find(s => s.name === uniqueName)) {
                      counter++
                      uniqueName = `${name}_${counter}`
                    }
                    // Create new set with unique name
                    const newSetId = createSet(tags, uniqueName)
                    setActiveSet(newSetId)
                  } else {
                    // Create new set with original name
                    const newSetId = createSet(tags, name)
                    setActiveSet(newSetId)
                  }
                }}
                followingOnly={followingOnly}
                followingCount={followingCount}
                onFollowingToggle={handleFollowingToggle}
              />
            </div>
          </div>
        )}
      </div>

      <BooruDetailModal
        post={selectedPost}
        open={!!selectedPost}
        onOpenChange={(open) => !open && closeSelectedPost()}
        onTagClick={handleTagAdd}
        onTagsAdd={handleTagsAdd}
        onNegativeTagClick={handleNegativeTagAdd}
        onOpenTagInNewSet={handleOpenTagInNewSet}
        onOpenTagInBackgroundSet={handleOpenTagInBackgroundSet}
        onNavigate={handleNavigate}
        canNavigate={canNavigate}
        allPosts={allPosts}
        bountySelectionActive={bountySelectionActive}
        commissionSelectionActive={commissionSelectionActive}
        onOpenEditors={() => handleViewModeChange("editors")}
        onAddToBounty={(post) => {
          const selected = selectBountyPost(post)
          if (!selected) {
            toast.error(tt("couldNotUseThisContentForABounty"))
            return
          }
          closeSelectedPost()
          useScriptLinkingStore.setState({ returnToEditor: true, returnToTab: "bounties" })
          handleViewModeChange("editors")
          toast.success(tt("contentAddedToBountyDraft"))
        }}
        onAddToCommission={(post) => {
          const selected = selectCommissionPost(post)
          if (!selected) {
            toast.error(tt("couldNotUseThisContentForACommission"))
            return
          }
          closeSelectedPost()
          useScriptLinkingStore.setState({ returnToEditor: true, returnToTab: "commissions" })
          handleViewModeChange("editors")
          toast.success(tt("contentAddedToCommissionDraft"))
        }}
      />

      {showScrollButtons && viewMode === "browse" && (
        <>
          {/* Left stack: Search Console + Mini Search + Sticky Header */}
          <div
            className={cn(
              "fixed left-4 z-40 flex flex-col gap-2 transition-all duration-500 ease-in-out",
              buttonsVisible ? "opacity-100" : "opacity-[0.15] hover:opacity-100",
            )}
            style={{
              bottom: showMiniSearch
                ? "calc(5rem + var(--sib-player-height, 0px) + var(--promo-strip-h, 0px))"
                : "calc(1rem + var(--sib-player-height, 0px) + var(--promo-strip-h, 0px))",
            }}
            onMouseEnter={resetFadeTimer}
            onTouchStart={resetFadeTimer}
          >
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-10 w-10 rounded-full bg-background/40 backdrop-blur-xl shadow-lg border border-border/30 hover:bg-white/20 transition-all",
                showSearchConsole && "bg-accent/70",
              )}
              onClick={() => setShowSearchConsole(true)}
              title={tt("openSearchConsole")}
            >
              <BookOpen className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-10 w-10 rounded-full bg-background/40 backdrop-blur-xl shadow-lg border border-border/30 hover:bg-white/20 transition-all",
                showMiniSearch && "bg-accent/70",
              )}
              onClick={handleToggleMiniSearch}
              title={tt("toggleMiniSearchBar")}
            >
              <Search className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-10 w-10 rounded-full bg-background/40 backdrop-blur-xl shadow-lg border border-border/30 hover:bg-white/20 transition-all",
                stickyHeader && "bg-accent/70",
              )}
              onClick={() => {
                setStickyHeader(!stickyHeader)
              }}
              title={stickyHeader ? tt("disableStickyHeader") : tt("enableStickyHeader")}
            >
              <Pin className="h-4 w-4" />
            </Button>
          </div>

          {/* Right stack: Hide UI (toggle) + Feedback + Page scroll */}
          <div
            className={cn(
              "fixed right-4 z-40 flex flex-col gap-2 transition-all duration-500 ease-in-out",
              buttonsVisible ? "opacity-100" : "opacity-[0.15] hover:opacity-100",
            )}
            style={{
              bottom: showMiniSearch
                ? "calc(5rem + var(--sib-player-height, 0px) + var(--promo-strip-h, 0px))"
                : "calc(1rem + var(--sib-player-height, 0px) + var(--promo-strip-h, 0px))",
            }}
            onMouseEnter={resetFadeTimer}
            onTouchStart={resetFadeTimer}
          >
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-full bg-background/40 backdrop-blur-xl shadow-lg border border-border/30 hover:bg-white/20 transition-all"
              onClick={() => setHideUI(!hideUI)}
              title={hideUI ? tt("showUI") : tt("hideUIH")}
            >
              {hideUI ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-full bg-background/40 backdrop-blur-xl shadow-lg border border-border/30 hover:bg-white/20 transition-all"
              onClick={() => setFeedbackModalOpen(true)}
              title={tt("sendFeedback")}
            >
              <Bug className="h-4 w-4" />
            </Button>
            {/* Glym chat. The badge is how an app-wide announcement from her
                (a new partnership, etc.) actually gets noticed — without it
                the message just sits unseen in a chat nobody reopened. Count
                comes from /api/glym/notifications; see hooks/use-glym-unread.ts. */}
            <Button
              variant="ghost"
              size="icon"
              className="relative h-10 w-10 rounded-full bg-background/40 backdrop-blur-xl shadow-lg border border-border/30 hover:bg-blue-500/40 transition-all"
              onClick={() => router.push("/glym")}
              title={tt("glym")}
            >
              <MessageSquare className="h-4 w-4" />
              {glymUnread > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow ring-2 ring-background"
                  aria-label={`${glymUnread} unread`}
                >
                  {glymUnread > 9 ? "9+" : glymUnread}
                </span>
              )}
            </Button>
          </div>
        </>
      )}

      <BatchDownloadModal
        isOpen={showBatchDownloadFavorites}
        onClose={() => setShowBatchDownloadFavorites(false)}
        posts={favoritesPosts}
        source="favorites"
        sourceName="Favorites"
      />

      <PipPlayer />

      <FeedbackModal open={feedbackModalOpen} onOpenChange={setFeedbackModalOpen} />

      {/* Day-one promo cards — Early Access, plus Glymtober during October (see
          lib/glymtober.ts) — shown one at a time in one shared popup frame
          instead of two independently-timed modals racing each other. */}
      <DayOnePromoStack />

      {/* Founder upsell — waits until the user's 2nd calendar day so it doesn't stack with the day-one cards */}
      <FounderPromoModal founderSlotsRemaining={founderSlots.remaining} founderSlotsClaimed={founderSlots.claimed} />

      {/* Founder+ pitch — shown once to an existing Founder who isn't Founder+ yet (see lib/badges/compute.ts isFounderPlus) */}
      <FounderPlusModal />

      {/* Membership promo modal — content driven by active campaign in admin */}
      <PromoModal featureFlag="promo_modal_enabled" />
      {viewMode === "browse" && (
        <MembershipPromoStrip
          isModalOpen={!!selectedPost}
          onFounderClick={() => {
            // Patreon mode: Founder is bought on Patreon; the subscribe page explains it.
            if (founderSlots.remaining > 0 && !patreonMode) {
              fetch("/api/subscriptions/founder-checkout", { method: "POST" })
                .then(r => r.json())
                .then(d => { if (d.url) window.location.href = d.url })
                .catch(() => {})
            } else {
              router.push(patreonMode ? PATREON_SUBSCRIBE_PATH : "/subscribe")
            }
          }}
          founderSlotsRemaining={founderSlots.remaining}
          founderSoldOut={founderSlots.remaining <= 0}
        />
      )}

      {viewMode === "browse" && <PartnerLinksBar hideUI={hideUI} />}

      {/* Swipe sideways near the bottom of the screen to step through the tag sets (settings: Swipe between tags) */}
      {viewMode === "browse" && <TagStepGesture />}
      {viewMode === "browse" && <SwipeTutorial hasResults={allPosts.length > 0} />}

      {/* Mini Search Bar */}
      <div
        className="fixed left-4 right-4 z-50"
        style={{ bottom: "calc(1rem + var(--sib-player-height, 0px))" }}
      >
        <MiniSearchBar
          isVisible={showMiniSearch}
          selectedSites={selectedSites}
          availableSites={miniSearchSites}
          onSitesChange={setSelectedSites}
          tags={searchTags}
          onTagsChange={handleMiniSearchTagsChange}
          onCreateSet={handleMiniSearchCreateSet}
          handMode={handMode}
          sets={sets}
          activeSetId={activeSetId}
          onSetChange={setActiveSet}
        />
      </div>

      {/* Search Console Modal */}
      <SearchConsoleModal
        isOpen={showSearchConsole}
        onClose={() => setShowSearchConsole(false)}
        onTagSelect={handleSearchConsoleTagSelect}
        onMultiTagSelect={handleSearchConsoleMultiTagSelect}
      />

      {/* Auth Dialog */}
      {authSource && (
        <BooruAuthDialog
          open={authDialogOpen}
          onOpenChange={setAuthDialogOpen}
          source={authSource}
          onSave={handleSaveCredentials}
        />
      )}

      {/* Script Mini Player - Global floating player */}
      {/* Bottom-right floating stack — stacks vertically when both visible */}
      <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-2 pointer-events-none">
        <div className="pointer-events-auto"><DeviceManualIndicator /></div>
        {viewMode !== "editors" && (
          <div className="pointer-events-auto"><ScriptMiniPlayer /></div>
        )}
      </div>
    </div>
  )
}
